import { UzButton, UzInput } from '@utazzo/ui';
import { ExternalLink } from 'lucide-react';
import React, { useEffect, useState } from 'react';

import { MEDIA_FOLDER, MediaFolderProvider } from '../website-cms/components/media-folder';
import { MediaDropField } from '../website-cms/components/MediaDropField';

import { type WebsiteEntry } from './settings.api';
import { useUpdateWebsiteSettings, useWebsiteSettings } from './use-settings';

const SOCIAL_PLATFORMS = ['instagram', 'facebook', 'x', 'youtube', 'linkedin', 'google'] as const;

const Field = ({
  label,
  children,
  hint,
}: {
  label: string;
  children: React.ReactNode;
  hint?: string;
}): React.JSX.Element => (
  <div>
    <label className="mb-1 block text-xs font-medium text-(--fg-muted)">{label}</label>
    {children}
    {hint && <p className="mt-1 text-[12px] text-(--fg-faint)">{hint}</p>}
  </div>
);

export const WebsiteSettingsTab = ({
  canWrite = true,
}: {
  canWrite?: boolean;
}): React.JSX.Element => {
  const { data, isLoading } = useWebsiteSettings();
  const save = useUpdateWebsiteSettings();

  const [logoMediaId, setLogoMediaId] = useState<string | null>(null);
  const [companyProfileMediaId, setCompanyProfileMediaId] = useState<string | null>(null);
  const [social, setSocial] = useState<Record<string, string>>({});
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [address, setAddress] = useState('');
  const [whatsapp, setWhatsapp] = useState('');
  const [copyright, setCopyright] = useState('');

  useEffect(() => {
    if (!data) return;
    const s: Record<string, string> = {};
    for (const item of data.social) s[item.platform] = item.href;
    setSocial(s);
    setPhone(data.contact.phone ?? '');
    setEmail(data.contact.email ?? '');
    setAddress(data.contact.address ?? '');
    setWhatsapp(data.contact.whatsapp ?? '');
    setCopyright(data.copyright ?? '');
  }, [data]);

  if (isLoading || !data) return <p className="p-6 text-sm text-(--fg-faint)">Loading…</p>;

  const onSave = (): void => {
    const entries: WebsiteEntry[] = [
      ...SOCIAL_PLATFORMS.map((p) => ({ key: `website.social.${p}`, value: social[p] ?? '' })),
      { key: 'website.contact.phone', value: phone },
      { key: 'website.contact.email', value: email },
      { key: 'website.contact.address', value: address },
      { key: 'website.contact.whatsapp', value: whatsapp },
      { key: 'website.copyright', value: copyright },
    ];
    if (logoMediaId) entries.push({ key: 'website.logo_media_id', value: logoMediaId });
    if (companyProfileMediaId)
      entries.push({ key: 'website.company_profile_media_id', value: companyProfileMediaId });
    save.mutate(entries);
  };

  return (
    <MediaFolderProvider folder={MEDIA_FOLDER.config}>
      <div className="flex max-w-2xl flex-col gap-6 py-6">
        <Field
          label="Logo"
          hint="Shown in the site header and footer. Uploads serve through the CDN."
        >
          {data.logoUrl && !logoMediaId && (
            <img
              src={data.logoUrl}
              alt="Current logo"
              className="mb-2 h-12 w-auto rounded border border-(--border) bg-(--z-50) p-1"
            />
          )}
          <MediaDropField value={logoMediaId} kind="image" onChange={setLogoMediaId} />
        </Field>

        <Field
          label="Company profile (PDF)"
          hint="The latest upload replaces the previous one. Shown as a 'Download Company Profile' button on the MICE page."
        >
          {data.companyProfilePdfUrl && !companyProfileMediaId && (
            <a
              href={data.companyProfilePdfUrl}
              target="_blank"
              rel="noreferrer noopener"
              className="text-primary mb-2 inline-flex items-center gap-1.5 text-sm font-medium underline underline-offset-2 hover:opacity-80"
            >
              View current company profile
              <ExternalLink className="h-3.5 w-3.5 shrink-0" aria-hidden />
            </a>
          )}
          <MediaDropField
            value={companyProfileMediaId}
            kind="document"
            accept="application/pdf,.pdf"
            onChange={setCompanyProfileMediaId}
          />
        </Field>

        <fieldset className="flex flex-col gap-3">
          <legend className="mb-1 text-xs font-semibold tracking-wide text-(--fg-muted) uppercase">
            Social links
          </legend>
          {SOCIAL_PLATFORMS.map((p) => (
            <Field key={p} label={p[0]!.toUpperCase() + p.slice(1)}>
              <UzInput
                value={social[p] ?? ''}
                placeholder={`https://${p === 'x' ? 'x.com' : `${p}.com`}/utazzo`}
                onChange={(e) => setSocial((prev) => ({ ...prev, [p]: e.target.value }))}
              />
            </Field>
          ))}
        </fieldset>

        <Field label="Contact phone">
          <UzInput value={phone} placeholder="+91 …" onChange={(e) => setPhone(e.target.value)} />
        </Field>
        <Field
          label="WhatsApp number"
          hint="Drives the floating WhatsApp button and the 'Chat With Us' button on the callback forms. Include the country code. Leave blank to hide them."
        >
          <UzInput
            value={whatsapp}
            placeholder="+91 98119 16164"
            onChange={(e) => setWhatsapp(e.target.value)}
          />
        </Field>
        <Field label="Contact email">
          <UzInput
            value={email}
            placeholder="hello@utazzo.com"
            onChange={(e) => setEmail(e.target.value)}
          />
        </Field>
        <Field label="Office address" hint="Shown in the footer brand bar.">
          <UzInput
            value={address}
            placeholder="Gurgaon Office, Haryana, India"
            onChange={(e) => setAddress(e.target.value)}
          />
        </Field>
        <Field label="Footer copyright">
          <UzInput value={copyright} onChange={(e) => setCopyright(e.target.value)} />
        </Field>

        <div className="flex items-center justify-end gap-3 border-t border-(--border) pt-4">
          {!canWrite && (
            <span className="text-xs text-(--fg-faint)">
              Read-only - you can view but not save.
            </span>
          )}
          <UzButton onClick={onSave} loading={save.isPending} disabled={!canWrite}>
            Save website settings
          </UzButton>
        </div>
      </div>
    </MediaFolderProvider>
  );
};
